import React from "react";
import { UseFormRegister } from "react-hook-form";
import styled from "styled-components";
import { Text } from "./Text";
import { Color, colors } from "../../StyleSheet";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../Store/modules/language";
import { toEnglishDigits } from "../../Utils/toEnglishDigits";

export type InputProps = {
  name: string;
  register: UseFormRegister<any>;
  disabled?: boolean;
  defaultValue?: string | number | null;
  placeholder?: string;
  label?: string;
  error?: string;
  type?: string;
  direction?: "rtl" | "ltr";
  hasBorder?: boolean;
  icon?: string;
  onIconPress?: () => void;
  color?: Color;
  iconColor?: Color;
  borderColor?: Color;
  labelColor?: Color;
  maxlength?: number;
  pattern?: string;
  isNumeric?: boolean;
  wrapperClassName?: string;
  inputClassName?: string;
  required?: boolean;
  onChangeText?: (value: string) => void;
  className?: string;
  inputBg?: Color;
};

export type StyledInputProps = Omit<InputProps, "register">;

const StyledWrapper = styled.div<{
  hasBorder: boolean;
  label?: string;
  borderColor?: Color;
  wrapperClassName?: string;
  inputClassName?: string;
  inputBg?: string;
}>`
  width: 100%;
  border: ${(props: any) =>
    props.hasBorder ? `1px solid ${props.theme["optBorderInput"]}` : "none"};
  outline: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 5px;
  background-color: ${({ theme }: any) => theme.inputBg};
`;

const StyledInput = styled.input<StyledInputProps>`
  width: 100%;
  padding: 0 10px;
  border-radius: 5px;
  outline: 0;
  height: 100%;
  color: ${({ theme }: any) => theme.text};
  direction: ${(props) => (props.direction ? props.direction : "rtl")};
  background-color: ${({ theme, inputBg }) =>
    inputBg ? (theme as any)[inputBg] : colors.transparent};
  font-size: 14px;
`;

export const Input: React.FC<InputProps> = (props) => {
  const {
    register,
    name,
    disabled,
    defaultValue,
    placeholder,
    label,
    error,
    type,
    hasBorder = true,
    color,
    borderColor,
    maxlength,
    pattern,
    required,
    onChangeText,
    className,
    wrapperClassName,
    inputClassName,
    inputBg,
  } = props;

  const { t } = useTranslation();
  const { direction, locale } = useLanguage();

  // const num_dic = {
  //   "۰": "0",
  //   "۱": "1",
  //   "۲": "2",
  //   "۳": "3",
  //   "۴": "4",
  //   "۵": "5",
  //   "۶": "6",
  //   "۷": "7",
  //   "۸": "8",
  //   "۹": "9",
  // };

  const reg = register(name);

  return (
    <div className={`w-full ${wrapperClassName ? wrapperClassName : ""}`} dir={direction}>
      {label ? (
        <div className="flex" dir={direction}>
          <Text elementType="label" className="text-[13px] mb-[8px]" fontWeight={600}>
            {label}
          </Text>
          {required ? (
            <Text elementType="span" _color="#EE384E" className="mx-1 text-xs">
              *
            </Text>
          ) : null}
        </div>
      ) : null}

      <StyledWrapper
        hasBorder={hasBorder}
        label={label}
        borderColor={borderColor}
        className={`${className ? className : ""} `}
      >
        <StyledInput
          id={name}
          {...reg}
          inputBg={inputBg}
          style={{ fontFamily: locale === "en" ? "Mulish, Estedad" : "Estedad, Mulish" }}
          name={name}
          className={`xs:text-[8px] ${inputClassName ?? ""}`}
          type={type}
          onChange={(e) => {
            const convertedValue = toEnglishDigits(e.target.value);

            if (e.target.value !== convertedValue) {
              const cursorPosition = e.target.selectionStart;
              e.target.value = convertedValue;
              setTimeout(() => {
                if (e.target.setSelectionRange) {
                  e.target.setSelectionRange(cursorPosition, cursorPosition);
                }
              }, 0);
            }
            reg.onChange(e);
            onChangeText && onChangeText(convertedValue);
          }}
          direction={direction}
          defaultValue={defaultValue as any}
          disabled={disabled}
          placeholder={placeholder ? (t as unknown as (k: string) => string)(placeholder) : ""}
          color={color as any}
          maxLength={maxlength}
          pattern={pattern}
        />
      </StyledWrapper>

      {error ? (
        <Text elementType="span" color="error" className="text-[11px]">
          {error}
        </Text>
      ) : null}
    </div>
  );
};
